import { ChangeDetectionStrategy, Component, DestroyRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyPipe, formatCurrency } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';

import { SalesService } from '@app/sales/sales.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { ApiRefusal, CashFamily, MethodTotal, PeriodRecap, StationBalance, closingGap, expectedFloat, lostCause, lostReply, mergeCashTotals, nonNegativeCents, refusalText, sumCents } from '@app/sales/reglement/reglement.models';
import { ReglementService } from '@app/sales/reglement/reglement.service';
import { CashCountGridComponent, CashCountResult } from '@app/sales/reglement/cash-count-grid/cash-count-grid.component';
import { CentsInputDirective } from '@app/sales/reglement/cents-input.directive';

export const CLOSE_WARNING = 'Vous êtes sur le point de clôturer cette période. Cette action est irréversible.';

// Format du tableau (pipe currency fr-FR) : le message d'une réponse perdue cite une valeur à y retrouver telle quelle.
const eur = (v: number) => formatCurrency(v, 'fr-FR', '€', 'EUR', '1.2-2');

export interface ZCloseDialogData {
	periodId: string;
	recap: PeriodRecap;
	// null : solde du poste illisible (balanceError dit pourquoi) ; le Z reste possible, sans versement.
	balance: StationBalance | null;
	balanceError: string;
	family: CashFamily | null;
}

// drop = total versé au coffre depuis cette boîte ; counted = fond du soir envoyé en cash_out.
export interface ZCloseResult {
	closed: true;
	drop: number;
	counted: number;
	// Z fait mais fond du soir non enregistré (msg d'une réponse ok du 1202) : à montrer, jamais à rejouer.
	warning: string | null;
}

@Component({
	selector: 'app-z-close-dialog',
	standalone: true,
	templateUrl: './z-close-dialog.component.html',
	styleUrls: ['./z-close-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, CurrencyPipe, CashCountGridComponent, CentsInputDirective],
})
export class ZCloseDialogComponent {

	private reglementService = inject(ReglementService);
	private salesService = inject(SalesService);
	private spin = inject(SpinnerService);
	private destroyRef = inject(DestroyRef);
	public dialogRef = inject(MatDialogRef<ZCloseDialogComponent, ZCloseResult>);
	public data: ZCloseDialogData = inject(MAT_DIALOG_DATA);
	readonly warning = CLOSE_WARNING;

	// Copie : un versement réussi suivi d'une clôture refusée entre dans « Versé au coffre » et sort des espèces attendues.
	public recap: PeriodRecap = this.data.recap;
	public readonly methods: MethodTotal[] = mergeCashTotals(this.data.recap.methods, this.data.family);
	public readonly count = this.data.recap.methods.reduce((n, m) => n + m.count, 0);
	public readonly safe = this.data.balance?.safe ?? null;
	public dropped = 0;
	public sending = false;
	public sendError = '';
	// Réponse perdue : versement ou Z peut-être enregistré ; la boîte ne renvoie plus rien, seul « Annuler » reste (le Z rouvert relit le récapitulatif).
	public lost = false;
	public form = new FormGroup({
		// Jamais prérempli (JP 04/10) : on verse ce qu'on veut, 0 compris ; sans coffre, fermé à 0.
		drop: new FormControl<number | null>({ value: this.safe ? null : 0, disabled: !this.safe }, [Validators.required, nonNegativeCents]),
		counted: new FormControl<number | null>(null, [Validators.required, nonNegativeCents]),
	});

	@ViewChild('dropGrid') dropGrid?: CashCountGridComponent;
	@ViewChild('countedGrid') countedGrid?: CashCountGridComponent;
	// Valeur du champ avant la première case remplie : une grille revidée la rend, jamais un 0 que personne n'a saisi.
	private beforeCount: Partial<Record<'drop' | 'counted', number | null>> = {};

	get dropCtl() { return this.form.controls.drop; }
	get countedCtl() { return this.form.controls.counted; }

	// null = saisie invalide ; champ fermé (pas de coffre) = 0.
	get drop(): number | null {
		if (this.dropCtl.disabled) return 0;
		return this.dropCtl.valid ? Number(this.dropCtl.value) : null;
	}

	get counted(): number | null {
		return this.countedCtl.valid ? Number(this.countedCtl.value) : null;
	}

	get expected(): number | null {
		const d = this.drop;
		return d === null ? null : expectedFloat(this.recap, d);
	}

	get gap(): number | null {
		const d = this.drop, c = this.counted;
		return d === null || c === null ? null : closingGap(this.recap, d, c);
	}

	get totalOut(): number {
		return sumCents([this.recap.transfers_out, this.drop ?? 0]);
	}

	// Une ligne de grille refusée bloque l'envoi : le champ porterait le total d'avant, sans elle.
	get canConfirm(): boolean {
		return this.form.valid && !this.dropGrid?.invalid && !this.countedGrid?.invalid && !this.sending && !this.lost;
	}

	confirm(): void {
		if (!this.canConfirm || this.spin.isLoading) return;
		const drop = this.drop ?? 0;
		const counted = this.counted!;
		const before = this.recap.transfers_out;
		let stage: 'drop' | 'close' = drop > 0 ? 'drop' : 'close';
		this.sending = true;
		this.sendError = '';
		this.dialogRef.disableClose = true;	// Échap couperait l'envoi et cacherait un versement ou un Z peut-être enregistré
		// Versement AVANT le 1202 : il tombe dans la session close et entre dans son récapitulatif.
		const drop$: Observable<unknown> = drop > 0
			? this.reglementService.postTransfer$({ source: this.data.balance!.station, destination: this.safe!.id, partner: null, amount: drop })
				.pipe(tap(() => { this.recordDrop(drop); stage = 'close'; }))
			: of(null);
		drop$.pipe(
			switchMap(() => this.salesService.postPeriodClose(this.data.periodId, counted)),
			map((r) => { if (r?.success === false) throw new ApiRefusal(r.error_msg || r.msg || 'Refus du serveur, sans message', r.error_code); return r; }),
			this.spin.block(drop > 0 ? 'Versement au coffre et fermeture de la caisse (Z)…' : 'Fermeture de la caisse (Z)…'),
			// Après une réponse perdue, Échap et le fond restent inactifs : seul « Annuler » ferme, et fait relire les écrans.
			finalize(() => { this.sending = false; this.dialogRef.disableClose = this.lost; }),
			takeUntilDestroyed(this.destroyRef),
		).subscribe({
			// Fermée AVANT la chute du voile : beforeClosed relève celui de la relecture, l'écran ne se libère qu'avec l'état réel.
			next: (r) => this.dialogRef.close({ closed: true, drop: this.dropped, counted, warning: r?.msg || null }),
			error: (e) => {
				this.lost = lostReply(e);
				this.sendError = this.lost
					? (stage === 'drop'
						? `Réponse perdue (${lostCause(e)}) : le versement de ${eur(drop)} a peut-être été enregistré. Annulez puis relancez le Z : s'il est passé, la ligne « − Versé au coffre » du tableau vaudra ${eur(sumCents([before, drop]))} au lieu de ${eur(before)} ; versez alors 0.`
						: `Réponse perdue (${lostCause(e)}) : la clôture a peut-être été faite. Annulez et regardez l'état de la période avant de réessayer.`)
					: stage === 'drop'
						? `Versement refusé, rien n'est clôturé : ${refusalText(e)}. Mettez le versement à 0 pour clôturer sans versement.`
						: `Clôture refusée : ${refusalText(e)}`;
			},
		});
	}

	// Versement enregistré : il ne doit plus être proposé ni renvoyé si l'on réessaie la clôture.
	private recordDrop(drop: number): void {
		this.dropped = sumCents([this.dropped, drop]);
		this.recap = { ...this.recap, transfers_out: sumCents([this.recap.transfers_out, drop]), expected_cash: sumCents([this.recap.expected_cash, -drop]) };
		this.dropCtl.setValue(0);
		this.dropGrid?.clear(false);
		delete this.beforeCount.drop;
	}

	// Le comptage remplit le champ ; le champ reste modifiable à la main (le harnais NRT y tape directement).
	onCount(field: 'drop' | 'counted', r: CashCountResult | null): void {
		const ctl = this.form.controls[field];
		const grid = field === 'drop' ? this.dropGrid : this.countedGrid;
		if (!r || ctl.disabled) return;
		if (!grid?.touched) {
			if (field in this.beforeCount) { ctl.setValue(this.beforeCount[field] ?? null); delete this.beforeCount[field]; }
			return;
		}
		if (!(field in this.beforeCount)) this.beforeCount[field] = ctl.value;
		ctl.setValue(r.total);
		ctl.markAsTouched();
	}

	// Montant tapé à la main qui ne correspond plus au comptage : signalé, jamais corrigé.
	differs(grid: CashCountGridComponent | undefined, value: number | null): boolean {
		return !!grid && grid.touched && !grid.invalid && value !== null && value !== grid.total;
	}

	// Entrée en fin de grille : vers le premier montant encore à saisir (versement d'abord), sinon vers le bouton.
	focusSubmit(): void {
		const missing = [this.dropCtl, this.countedCtl].find((c) => c.enabled && c.invalid);
		missing?.markAsTouched();
		const id = !missing ? 'z-submit' : missing === this.dropCtl ? 'z-drop' : 'z-counted';
		document.getElementById(id)?.focus();
	}

	cancel(): void {
		if (this.sending) return;
		if (this.lost) this.reglementService.markUncertain('transfer');
		this.dialogRef.close();
	}
}
